import { aspectRatioFilterValueToDecimal } from "@lib/mediaLibrary";

import {
  extractLiquidMessageComponentNames,
  isExactLiquidMessageComponentTag,
} from "./extractLiquidMessageComponentNames";
import { listActiveComponentEntries } from "./listActiveComponentEntries";
import { getPushImageVariantRules } from "./pushImageVariantRules";
import { resolveComponentName } from "./resolveComponentName";
import type {
  ComponentStoreData,
  ComponentValueEntry,
  PushImageMessagePlacement,
  PushImageMessagePlacementValidationResult,
  PushImageVariantValidationResult,
  VariantMetadata,
} from "./types";

const imageComponentName = "image";

const imageRatioTolerance = 0.1;

const containsImageComponentTag = (value: string | null | undefined): boolean => {
  if (typeof value !== "string") {
    return false;
  }

  return Array.from(extractLiquidMessageComponentNames(value)).some(
    (componentName) => !!resolveComponentName(componentName, [imageComponentName])
  );
};

/** Validates Push Image variants for one component store. */
export class PushImageVariantValidator {
  private readonly activeImageEntries: ComponentValueEntry[];
  private readonly imageMetadata: VariantMetadata[];

  constructor(componentStoreData: Partial<ComponentStoreData> | null) {
    const imageComponentEntry = Object.entries(componentStoreData?.components ?? {}).find(
      ([componentName]) => componentName.toLowerCase() === imageComponentName
    );
    const [imageComponentKey, imageValues] = imageComponentEntry ?? [undefined, []];
    const deactivatedValues = new Set(
      imageComponentKey ? (componentStoreData?.deactivatedValues?.[imageComponentKey] ?? []) : []
    );

    this.activeImageEntries = listActiveComponentEntries(imageValues ?? [], deactivatedValues);
    this.imageMetadata = imageComponentKey ? (componentStoreData?.variantMetadata?.[imageComponentKey] ?? []) : [];
  }

  /**
   * Checks for a misplaced or absent Image component tag.
   *
   * @param placement - Supported and unsupported fields for the Push message
   * @returns The Image component placement validation result
   */
  validateMessagePlacement(placement: PushImageMessagePlacement | null): PushImageMessagePlacementValidationResult {
    if (!placement || this.activeImageEntries.length === 0) {
      return { hasInvalidTarget: false, hasMissingPlacement: false, unsupportedFields: [] };
    }

    return {
      hasInvalidTarget: placement.platform === "kindle",
      hasMissingPlacement:
        placement.platform !== "kindle" &&
        !placement.supportedFields.some(({ value }) => isExactLiquidMessageComponentTag(value, imageComponentName)),
      unsupportedFields: placement.unsupportedFields
        .filter(({ value }) => containsImageComponentTag(value))
        .map(({ field }) => field),
    };
  }

  /**
   * Validates direct Push Image placement for the selected Push targets.
   *
   * @param placements - Push message placements for the selected targets
   * @returns The Push Image variant validation result
   */
  validateGuardrails(placements: Array<PushImageMessagePlacement | null>): PushImageVariantValidationResult {
    const validPlacements = placements.filter(
      (placement): placement is PushImageMessagePlacement => placement !== null
    );
    if (validPlacements.length === 0) {
      return {
        hasMissingPlacement: false,
        hasUnsupportedPlacement: false,
        hasInvalidTargets: false,
        hasIncompatibleVariants: false,
      };
    }

    const placementValidations = validPlacements.map((placement) => this.validateMessagePlacement(placement));
    const platforms = validPlacements.map(({ platform }) => platform);
    const hasIncompatibleVariants = this.activeImageEntries.some(({ value, index }) => {
      const metadata = this.imageMetadata[index];
      const rules = getPushImageVariantRules({
        platforms,
        url: value,
        storedFileType: metadata?.file_type,
      });
      const hasIncompatibleFileType =
        rules.fileTypeIsInvalid || (rules.requiresFileType && !rules.effectiveFileType);
      const actualRatio = Number(metadata?.aspect_ratio);
      const expectedRatio = rules.expectedRatio ? aspectRatioFilterValueToDecimal[rules.expectedRatio] : undefined;
      const hasIncompatibleRatio = Boolean(
        expectedRatio &&
          metadata?.aspect_ratio &&
          Number.isFinite(actualRatio) &&
          Math.abs(actualRatio - expectedRatio) > imageRatioTolerance
      );

      return hasIncompatibleFileType || hasIncompatibleRatio;
    });

    return {
      hasMissingPlacement: placementValidations.some(({ hasMissingPlacement }) => hasMissingPlacement),
      hasUnsupportedPlacement: placementValidations.some(({ unsupportedFields }) => unsupportedFields.length > 0),
      hasInvalidTargets:
        this.activeImageEntries.length > 0 && placementValidations.some(({ hasInvalidTarget }) => hasInvalidTarget),
      hasIncompatibleVariants,
    };
  }
}
